// src/LiveTableFeed.jsx  (FINAL - PERFECTED OLD/NEW DETECTION - COPY/PASTE READY)
// ✅ Keeps your UI / QR / voucher logic
// ✅ Stronger canonical phone matching
// ✅ Works with contacts_routes.py: { items: [...] } and legacy { contacts: [...] }
// ✅ Searches backend with multiple aliases
// ✅ Tries phone-specific search first, then generic fallback
// ✅ Handles string / array / object phone shapes
// ✅ Stable OLD / NEW classification for table contacts from SelectContactsModal

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./LiveTableFeed.css";
import { apiJSON, getActiveBranch } from "./apiBase";
import MenuQrModal from "./MenuQrModal";

/** @typedef {{table:string, hostess:string, waiter:string, contacts:(string[]|Array<any>), time:string, branch?:string}} LogItem */

const BRANCH_OPTIONS = [
  { value: "jounieh", label: "Jounieh" },
  { value: "batroun", label: "Batroun" },
  { value: "zgharta", label: "Zgharta" },
  { value: "tripoli", label: "Tripoli" },
  { value: "ehden", label: "Ehden" },
];

const BRANCH_LABEL_MAP = {
  jounieh: "Jounieh",
  batroun: "Batroun",
  zgharta: "Zgharta",
  tripoli: "Tripoli",
  ehden: "Ehden",
};

const BRANCH_MENU_PAGE = {
  jounieh: "https://api-jounieh.bamboome.me/menu-page",
  batroun: "https://api-batroun.bamboome.me/menu-page",
  tripoli: "https://api-tripoli.bamboome.me/menu-page",
  zgharta: "https://api-zgharta.bamboome.me/menu-page",
  ehden: "https://api-ehden.bamboome.me/menu-page",
};

const VOUCHER_API_BASE = "https://api-jounieh.bamboome.me";

const PHONE_STATUS_CACHE_VERSION = 6;
const PHONE_STATUS_TTL_MS = 10 * 60 * 1000;
const CONTACTS_FETCH_LIMIT = 300;
const CONTACTS_FETCH_MAX_LOOPS = 8;

const WEBHOOK_STATUS_CACHE_VERSION = 2;
const WEBHOOK_STATUS_TTL_MS = 5 * 60 * 1000;

const detectBranchFromHost = () => {
  if (typeof window === "undefined") return null;
  const host = (window.location.hostname || "").toLowerCase();

  for (const b of Object.keys(BRANCH_MENU_PAGE)) {
    if (host.startsWith(`${b}.`) || host.includes(`.${b}.`) || host.includes(`${b}.bamboome.me`)) {
      return b;
    }
  }

  if (host.includes("api-jounieh")) return "jounieh";
  if (host.includes("api-batroun")) return "batroun";
  if (host.includes("api-tripoli")) return "tripoli";
  if (host.includes("api-zgharta")) return "zgharta";
  if (host.includes("api-ehden")) return "ehden";

  return null;
};

const safeArray = (x) => (Array.isArray(x) ? x : []);

const parseTimeMs = (t) => {
  if (!t) return 0;
  const s = String(t).trim();
  const isoGuess = s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
  const ms = Date.parse(isoGuess);
  return Number.isFinite(ms) ? ms : 0;
};

const normalizeDigits = (s) => String(s || "").replace(/\D/g, "");

const normalizePhoneForCompare = (input) => {
  let d = normalizeDigits(input);
  if (!d) return "";

  if (d.startsWith("00")) d = d.slice(2);

  if (d.startsWith("961")) {
    let rest = d.slice(3).replace(/^0+/, "");
    if (!rest) return "961";
    if (rest.length > 8) rest = rest.slice(-8).replace(/^0+/, "");
    return `961${rest}`;
  }

  if (d.startsWith("0")) {
    const rest = d.replace(/^0+/, "");
    if (!rest) return "";
    if (rest.length <= 8) return `961${rest}`;
    return d;
  }

  if (d.length === 7 || d.length === 8) return `961${d}`;

  return d;
};

const withPlus = (display) => {
  if (!display) return "";
  const n = normalizePhoneForCompare(display);
  if (!n) return "";
  return `+${n}`;
};

const extractPhoneCandidates = (text) => {
  const matches = String(text || "").match(/\+?\d[\d\s\-()]{5,}\d/g) || [];
  return matches
    .map((m) => normalizePhoneForCompare(m))
    .filter(Boolean);
};

const extractFirstPhone = (text) => {
  const all = extractPhoneCandidates(text);
  const first = all[0] || "";
  return { display: first, href: first };
};

const extractBirthday = (text) => {
  const s = String(text || "");
  const mmdd = s.match(/--\d{2}-\d{2}/);
  if (mmdd) return mmdd[0];
  const ymd = s.match(/\b\d{4}-\d{2}-\d{2}\b/);
  if (ymd) return ymd[0];
  const alt = s.match(/\b\d{1,2}[\/.-]\d{1,2}\b/);
  if (alt) return alt[0];
  return "";
};

const cleanName = (text, phoneDisplay, bday) => {
  let name = String(text || "");
  if (phoneDisplay) name = name.replace(phoneDisplay, " ");
  if (bday) name = name.replace(bday, " ");
  name = name.replace(/[|\-–—•]+/g, " ");
  name = name.replace(/🎂/g, " ");
  name = name.replace(/\s{2,}/g, " ").trim();
  return name;
};

const normalizeContact = (c) => {
  if (c && typeof c === "object") {
    const rawPhone =
      c.phones || c.phone || c.mobile || c.telephone || c.whatsapp || c.phone_number || "";
    const canonical = normalizePhoneForCompare(rawPhone);
    const bday = c.birthday ? String(c.birthday) : "";
    const name = String(c.name || "").trim() || cleanName(`${c.name || ""} ${canonical} ${bday}`, canonical, bday);

    return {
      name,
      phoneDisplay: canonical,
      phoneHref: canonical,
      birthday: bday,
    };
  }

  const raw = String(c ?? "");
  const { display, href } = extractFirstPhone(raw);
  const bday = extractBirthday(raw);
  const name = cleanName(raw, display, bday);
  return { name, phoneDisplay: display, phoneHref: href, birthday: bday };
};

const getPhoneKey = (phoneHref) => normalizePhoneForCompare(phoneHref);

const buildPhoneAliases = (phone) => {
  const set = new Set();
  const canonical = normalizePhoneForCompare(phone);
  if (!canonical) return set;

  const digits = normalizeDigits(canonical);
  const last8 = canonical.slice(-8);

  set.add(canonical);
  set.add(digits);
  if (last8) set.add(last8);

  if (canonical.startsWith("961")) {
    const local = canonical.slice(3);
    if (local) {
      set.add(local);
      set.add(`0${local}`);
      set.add(`961${local}`);
      set.add(`+961${local}`);
    }
  } else {
    set.add(`+${digits}`);
  }

  return set;
};

const buildSearchTermsForContacts = (phone) => {
  const aliases = Array.from(buildPhoneAliases(phone))
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  aliases.sort((a, b) => b.length - a.length);

  const uniq = [];
  const seen = new Set();
  for (const x of aliases) {
    const k = x.replace(/^\+/, "");
    if (seen.has(k)) continue;
    seen.add(k);
    uniq.push(x);
  }
  return uniq;
};

const valueToPhoneStrings = (value) => {
  if (!value) return [];

  if (typeof value === "string" || typeof value === "number") {
    return [normalizePhoneForCompare(value)].filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.flatMap((x) => valueToPhoneStrings(x));
  }

  if (typeof value === "object") {
    const out = [];
    for (const k of ["phone", "phones", "number", "mobile", "telephone", "value", "whatsapp", "phone_number"]) {
      if (value[k]) out.push(...valueToPhoneStrings(value[k]));
    }
    return out;
  }

  return [];
};

const collectRowPhoneCandidates = (row) => {
  const rawValues = [
    row?.phones,
    row?.phone,
    row?.mobile,
    row?.telephone,
    row?.whatsapp,
    row?.phone_number,
    row?.numbers,
  ];

  const candidates = rawValues.flatMap((v) => valueToPhoneStrings(v));
  const dedup = [];
  const seen = new Set();

  for (const c of candidates) {
    const n = normalizePhoneForCompare(c);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    dedup.push(n);
  }

  return dedup;
};

const rowMatchesPhone = (row, guestPhoneHref) => {
  const guestAliases = buildPhoneAliases(guestPhoneHref);
  if (!guestAliases.size) return false;

  const rowCandidates = collectRowPhoneCandidates(row);

  for (const candidate of rowCandidates) {
    const aliases = buildPhoneAliases(candidate);
    for (const a of aliases) {
      if (guestAliases.has(a)) return true;
    }
  }

  return false;
};

const classifyGuestAgainstRows = (guestPhoneHref, rows) => {
  const canonical = normalizePhoneForCompare(guestPhoneHref);
  if (!canonical) return "error";

  for (const row of safeArray(rows)) {
    if (rowMatchesPhone(row, canonical)) return "old";
  }
  return "new";
};

const buildTelHref = (phoneHref) => `tel:+${normalizeDigits(phoneHref)}`;
const buildSmsHref = (phoneHref) => `sms:+${normalizeDigits(phoneHref)}`;
const buildWaHref = (phoneHref) => {
  const digits = normalizeDigits(phoneHref);
  return digits ? `https://wa.me/${digits}` : "#";
};

const buildWaFeedbackHref = (phoneHref, name, branchLabel) => {
  const digits = normalizeDigits(phoneHref);
  if (!digits) return "#";

  const safeName = name || "there";
  const safeBranch = branchLabel || "Jounieh";

  const text = `Dear ${safeName}

Please I would like to find out, if you and your friends had a good time at Bamboo ${safeBranch} & all was ok.

Please your feedback is very important to us.

Best regards
Bamboo Asian cuisine 🎋
Fouad G Hanein`;

  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const extractContactsArray = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.contacts)) return data.contacts;
  return [];
};

const mergeUniqueRowsByIdOrPhone = (rows) => {
  const out = [];
  const seen = new Set();

  for (const row of safeArray(rows)) {
    const phonesKey = collectRowPhoneCandidates(row).sort().join("|");
    const nameKey = String(row?.name || "").trim().toLowerCase();
    const key =
      row?.id != null
        ? `id:${row.id}`
        : phonesKey
        ? `ph:${phonesKey}`
        : `nm:${nameKey}`;

    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }

  return out;
};

const parseMonthDay = (birthdayStr) => {
  const s = String(birthdayStr || "").trim();
  if (!s) return null;

  let m = s.match(/^--(\d{2})-(\d{2})$/);
  if (m) return { mm: Number(m[1]), dd: Number(m[2]) };

  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return { mm: Number(m[2]), dd: Number(m[3]) };

  m = s.match(/^(\d{1,2})[\/.-](\d{1,2})$/);
  if (m) return { mm: Number(m[1]), dd: Number(m[2]) };

  return null;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const nextBirthdayDate = (mm, dd, now) => {
  const y = now.getFullYear();
  let candidate = new Date(y, mm - 1, dd);

  if (candidate.getMonth() !== mm - 1 || candidate.getDate() !== dd) {
    candidate = new Date(y + 1, mm - 1, dd);
    if (candidate.getMonth() !== mm - 1 || candidate.getDate() !== dd) return null;
  }

  if (startOfDay(candidate) < startOfDay(now)) {
    const next = new Date(y + 1, mm - 1, dd);
    if (next.getMonth() !== mm - 1 || next.getDate() !== dd) return null;
    return startOfDay(next);
  }
  return startOfDay(candidate);
};

const lastBirthdayDate = (mm, dd, now) => {
  const y = now.getFullYear();
  let candidate = new Date(y, mm - 1, dd);

  if (candidate.getMonth() !== mm - 1 || candidate.getDate() !== dd) {
    candidate = new Date(y - 1, mm - 1, dd);
    if (candidate.getMonth() !== mm - 1 || candidate.getDate() !== dd) return null;
  }

  if (startOfDay(candidate) > startOfDay(now)) {
    const prev = new Date(y - 1, mm - 1, dd);
    if (prev.getMonth() !== mm - 1 || prev.getDate() !== dd) return null;
    return startOfDay(prev);
  }
  return startOfDay(candidate);
};

const diffDays = (a, b) => {
  const ms = startOfDay(a).getTime() - startOfDay(b).getTime();
  return Math.round(ms / 86400000);
};

const getOldGuestBirthdayFlags = (birthdayStr, status) => {
  if (status !== "old") return { recent30: false, next7: false, recentDaysAgo: null, nextInDays: null };

  const md = parseMonthDay(birthdayStr);
  if (!md) return { recent30: false, next7: false, recentDaysAgo: null, nextInDays: null };

  const now = new Date();
  const last = lastBirthdayDate(md.mm, md.dd, now);
  const next = nextBirthdayDate(md.mm, md.dd, now);

  const daysAgo = last ? diffDays(now, last) : null;
  const daysUntil = next ? diffDays(next, now) : null;

  const recent30 = typeof daysAgo === "number" && daysAgo >= 0 && daysAgo <= 30;
  const next7 = typeof daysUntil === "number" && daysUntil >= 0 && daysUntil <= 7;

  return { recent30, next7, recentDaysAgo: daysAgo, nextInDays: daysUntil };
};

const fmtYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getVoucherDateForOldBirthday = (birthdayStr, status) => {
  if (status !== "old") return "";
  const md = parseMonthDay(birthdayStr);
  if (!md) return "";

  const now = new Date();
  const flags = getOldGuestBirthdayFlags(birthdayStr, status);

  if (flags.next7) {
    const next = nextBirthdayDate(md.mm, md.dd, now);
    return next ? fmtYMD(next) : "";
  }
  if (flags.recent30) {
    const last = lastBirthdayDate(md.mm, md.dd, now);
    return last ? fmtYMD(last) : "";
  }
  return "";
};

const rewriteToJounieh = (u) => {
  if (!u) return "";
  try {
    const parsed = new URL(String(u));
    parsed.host = "api-jounieh.bamboome.me";
    parsed.protocol = "https:";
    return parsed.toString();
  } catch {
    return String(u);
  }
};

const btnStyle = (bg) => ({
  backgroundColor: bg,
  color: "#fff",
  border: "1px solid rgba(0,0,0,.1)",
  padding: "6px 10px",
  borderRadius: 8,
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 13,
  lineHeight: 1.2,
  boxShadow: "0 1px 3px rgba(0,0,0,.15)",
});

export default function LiveTableFeed() {
  const [logs, setLogs] = useState([]);
  const [cleared, setCleared] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [openActionKey, setOpenActionKey] = useState(null);

  const hostLockedBranch = useMemo(() => detectBranchFromHost(), []);
  const initialBranch = useMemo(() => {
    const inferred = (hostLockedBranch || getActiveBranch?.() || "jounieh").toLowerCase();
    return BRANCH_OPTIONS.some((x) => x.value === inferred) ? inferred : "jounieh";
  }, [hostLockedBranch]);

  const [branchCode, setBranchCode] = useState(initialBranch);
  const [serverResolvedBranch, setServerResolvedBranch] = useState("");
  const serverLocked = useMemo(() => {
    if (hostLockedBranch) return true;
    if (serverResolvedBranch && serverResolvedBranch !== branchCode) return true;
    return false;
  }, [hostLockedBranch, serverResolvedBranch, branchCode]);

  const [phoneStatusByBranch, setPhoneStatusByBranch] = useState(() => ({}));
  const phoneStatusRef = useRef(phoneStatusByBranch);
  const [checking, setChecking] = useState(null);

  const [voucherLinks, setVoucherLinks] = useState({});
  const [voucherBusyKey, setVoucherBusyKey] = useState(null);

  const [webhookStatusByBranch, setWebhookStatusByBranch] = useState(() => ({}));
  const webhookStatusRef = useRef(webhookStatusByBranch);
  const [webhookChecking, setWebhookChecking] = useState(null);

  useEffect(() => {
    phoneStatusRef.current = phoneStatusByBranch;
  }, [phoneStatusByBranch]);

  useEffect(() => {
    webhookStatusRef.current = webhookStatusByBranch;
  }, [webhookStatusByBranch]);

  const CACHE_KEY = useMemo(
    () => `liveFeedPhoneStatus:v${PHONE_STATUS_CACHE_VERSION}:${branchCode}`,
    [branchCode]
  );

  const WEBHOOK_CACHE_KEY = useMemo(
    () => `liveFeedWebhookStatus:v${WEBHOOK_STATUS_CACHE_VERSION}:${branchCode}`,
    [branchCode]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const src = parsed && typeof parsed === "object" ? parsed : {};
      const now = Date.now();

      const cleaned = {};
      for (const [k, v] of Object.entries(src)) {
        if (!v || typeof v !== "object") continue;
        if (!v.status) continue;
        if (!v.ts || now - Number(v.ts) > PHONE_STATUS_TTL_MS) continue;
        cleaned[k] = v;
      }

      setPhoneStatusByBranch((prev) => ({ ...prev, [branchCode]: cleaned }));
    } catch {
      setPhoneStatusByBranch((prev) => ({ ...prev, [branchCode]: {} }));
    }
  }, [CACHE_KEY, branchCode]);

  useEffect(() => {
    try {
      const brMap = phoneStatusByBranch?.[branchCode] || {};
      localStorage.setItem(CACHE_KEY, JSON.stringify(brMap));
    } catch {}
  }, [CACHE_KEY, branchCode, phoneStatusByBranch]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WEBHOOK_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const src = parsed && typeof parsed === "object" ? parsed : {};
      const now = Date.now();

      const cleaned = {};
      for (const [k, v] of Object.entries(src)) {
        if (!v || typeof v !== "object") continue;
        if (!v.ts || now - Number(v.ts) > WEBHOOK_STATUS_TTL_MS) continue;
        cleaned[k] = v;
      }

      setWebhookStatusByBranch((prev) => ({ ...prev, [branchCode]: cleaned }));
    } catch {
      setWebhookStatusByBranch((prev) => ({ ...prev, [branchCode]: {} }));
    }
  }, [WEBHOOK_CACHE_KEY, branchCode]);

  useEffect(() => {
    try {
      const brMap = webhookStatusByBranch?.[branchCode] || {};
      localStorage.setItem(WEBHOOK_CACHE_KEY, JSON.stringify(brMap));
    } catch {}
  }, [WEBHOOK_CACHE_KEY, branchCode, webhookStatusByBranch]);

  const getStatus = useCallback((branch, key) => {
    const entry = phoneStatusRef.current?.[branch]?.[key] || null;
    if (!entry) return null;
    if (!entry.ts || Date.now() - Number(entry.ts) > PHONE_STATUS_TTL_MS) return null;
    return entry.status || null;
  }, []);

  const setStatus = useCallback((branch, key, value) => {
    setPhoneStatusByBranch((prev) => {
      const cur = prev?.[branch] && typeof prev[branch] === "object" ? prev[branch] : {};
      return {
        ...prev,
        [branch]: {
          ...cur,
          [key]: { status: value, ts: Date.now() },
        },
      };
    });
  }, []);

  const getWebhookStatus = useCallback((branch, key) => {
    const entry = webhookStatusRef.current?.[branch]?.[key] || null;
    if (!entry) return null;
    if (!entry.ts || Date.now() - Number(entry.ts) > WEBHOOK_STATUS_TTL_MS) return null;
    return entry || null;
  }, []);

  const setWebhookStatus = useCallback((branch, key, value) => {
    setWebhookStatusByBranch((prev) => {
      const cur = prev?.[branch] && typeof prev[branch] === "object" ? prev[branch] : {};
      return {
        ...prev,
        [branch]: {
          ...cur,
          [key]: { ...value, ts: Date.now() },
        },
      };
    });
  }, []);

  const clearWebhookStatusKeys = useCallback((branch, keys) => {
    setWebhookStatusByBranch((prev) => {
      const cur = prev?.[branch] && typeof prev[branch] === "object" ? { ...prev[branch] } : {};
      let changed = false;
      keys.forEach((k) => {
        if (k in cur) {
          delete cur[k];
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, [branch]: cur };
    });
  }, []);

  const clearStatusKeys = useCallback((branch, keys) => {
    setPhoneStatusByBranch((prev) => {
      const cur = prev?.[branch] && typeof prev[branch] === "object" ? { ...prev[branch] } : {};
      let changed = false;
      keys.forEach((k) => {
        if (k in cur) {
          delete cur[k];
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, [branch]: cur };
    });
  }, []);

  const makeBranchHeaders = useCallback(
    (br) => {
      const b = (br || "").toLowerCase();
      if (!b) return undefined;
      if (hostLockedBranch) return undefined;
      if (serverResolvedBranch && serverResolvedBranch !== b) return undefined;
      return { "x-branch": b };
    },
    [hostLockedBranch, serverResolvedBranch]
  );

  const fetchLogs = useCallback(async () => {
    const br = (branchCode || "jounieh").toLowerCase();

    const data = await apiJSON("/table-log", {
      method: "GET",
      cache: "no-store",
      headers: makeBranchHeaders(br),
    });

    const resolved = (data?.branch || "").toString().toLowerCase();
    if (resolved && BRANCH_LABEL_MAP[resolved]) setServerResolvedBranch(resolved);

    const arr = safeArray(data?.logs).map((l) => ({
      ...l,
      branch: br,
      _timeMs: parseTimeMs(l?.time),
    }));

    arr.sort((a, b) => (b?._timeMs || 0) - (a?._timeMs || 0));
    return arr;
  }, [branchCode, makeBranchHeaders]);

  useEffect(() => {
    let alive = true;

    const tick = async () => {
      try {
        const arr = await fetchLogs();
        if (!alive) return;
        setLogs(arr);
      } catch (err) {
        console.error("Error fetching /table-log:", err);
      }
    };

    tick();
    const id = setInterval(tick, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [fetchLogs]);

  useEffect(() => {
    if (!serverResolvedBranch) return;
    if (serverResolvedBranch !== branchCode) setBranchCode(serverResolvedBranch);
  }, [serverResolvedBranch, branchCode]);

  useEffect(() => {
    const onDocClick = (e) => {
      const el = e.target;
      if (el && el.closest && el.closest(".phone-actions")) return;
      setOpenActionKey(null);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const copyText = async (txt) => {
    try {
      if (!txt) return;
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(txt);
      else {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      alert("✅ Copied");
    } catch {
      alert("❌ Copy failed");
    }
  };

  const onClickPhone = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenActionKey((prev) => (prev === key ? null : key));
  }, []);

  const recheckTable = useCallback(
    (log) => {
      if (!log || !Array.isArray(log.contacts)) return;
      const br = (branchCode || "jounieh").toLowerCase();
      const keysToReset = new Set();

      (log.contacts || []).forEach((c) => {
        const { phoneHref } = normalizeContact(c);
        const key = getPhoneKey(phoneHref);
        if (key) keysToReset.add(key);
      });

      if (!keysToReset.size) return;
      clearStatusKeys(br, keysToReset);
      clearWebhookStatusKeys(br, keysToReset);
    },
    [branchCode, clearStatusKeys, clearWebhookStatusKeys]
  );

  const canCopyLog = useCallback(
    (log) => {
      if (!log || !Array.isArray(log.contacts)) return true;
      const br = (branchCode || "jounieh").toLowerCase();
      for (const c of log.contacts) {
        const { phoneHref } = normalizeContact(c);
        const key = getPhoneKey(phoneHref);
        if (!key) continue;
        const status = getStatus(br, key);
        if (!status || status === "checking") return false;
      }
      return true;
    },
    [branchCode, getStatus]
  );

  const buildClipboardTextFromLog = useCallback(
    (log) => {
      const lines = [];
      if (log?.table) lines.push(String(log.table));
      if (log?.time) lines.push(String(log.time));
      if (log?.hostess) lines.push(String(log.hostess));
      if (log?.waiter) lines.push(String(log.waiter));

      const guestsCount = Array.isArray(log?.contacts) ? log.contacts.length : 0;
      const br = (branchCode || "jounieh").toLowerCase();

      lines.push(`Branch: ${BRANCH_LABEL_MAP[br] || br}`);
      lines.push(`${guestsCount} guests`);
      lines.push("");

      (log?.contacts || []).forEach((c) => {
        const { name, phoneDisplay, phoneHref, birthday } = normalizeContact(c);
        const phoneKey = getPhoneKey(phoneHref);
        const status = phoneKey ? getStatus(br, phoneKey) : null;
        const webhookStatus = phoneKey ? getWebhookStatus(br, phoneKey) : null;

        const bFlags = getOldGuestBirthdayFlags(birthday, status);
        const bNote =
          status === "old" && (bFlags.recent30 || bFlags.next7)
            ? ` [BDAY: ${bFlags.recent30 ? `last ${bFlags.recentDaysAgo}d` : ""}${bFlags.recent30 && bFlags.next7 ? " + " : ""}${bFlags.next7 ? `next ${bFlags.nextInDays}d` : ""}]`
            : "";

        if (name) lines.push(name + bNote);

        if (phoneDisplay) {
          const phoneLine = withPlus(phoneDisplay);
          let statusLabel = "";
          if (status === "old") statusLabel = " (🟡 ***OLD***)";
          else if (status === "new") statusLabel = " (🟢 ***NEW***)";
          else if (status === "error") statusLabel = " (******)";
          else if (status === "checking") statusLabel = " (**checking...**)";

          let webhookLabel = "";
          if (webhookStatus?.messaged_before) {
            const templatePart = webhookStatus.last_template ? ` + 🧩 ${webhookStatus.last_template}` : "";
            webhookLabel = ` [📩 ${String(webhookStatus.last_status || "messaged").toUpperCase()}${templatePart}${webhookStatus.has_replied ? " + 💬 REPLIED" : ""}]`;
          } else if (webhookStatus && !webhookStatus.loading && !webhookStatus.error) {
            webhookLabel = " [🆕 NEVER MESSAGED]";
          }

          lines.push(phoneLine + statusLabel + webhookLabel);
        }

        if (birthday) lines.push(`🎂 ${birthday}`);
        lines.push("");
      });

      while (lines.length && lines[lines.length - 1] === "") lines.pop();
      return lines.join("\n");
    },
    [branchCode, getStatus]
  );

  const copyOne = useCallback(
    async (index) => {
      try {
        const log = logs[index];
        if (!canCopyLog(log)) {
          alert("⏳ Still checking numbers for this table. Please wait a moment.");
          return;
        }

        const text = buildClipboardTextFromLog(log);

        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
        else {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }

        setCopiedIdx(index);
        setTimeout(() => setCopiedIdx(null), 1200);
      } catch (e) {
        console.error("Copy failed:", e);
        alert("❌ Could not copy to clipboard.");
      }
    },
    [logs, canCopyLog, buildClipboardTextFromLog]
  );

  const fetchVoucherLink = useCallback(
    async ({ date, name, phoneDigits }) => {
      if (!date || !name || !phoneDigits) return null;

      const cacheKey = `${phoneDigits}|${date}`;
      if (voucherLinks[cacheKey]) return voucherLinks[cacheKey];

      setVoucherBusyKey(cacheKey);
      try {
        const url =
          `${VOUCHER_API_BASE}/voucher/link` +
          `?date=${encodeURIComponent(date)}` +
          `&name=${encodeURIComponent(name)}` +
          `&phone=${encodeURIComponent(phoneDigits)}`;

        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`Voucher link failed (${res.status}) ${t || ""}`);
        }

        const data = await res.json();
        const out = {
          page_url: rewriteToJounieh(data?.page_url || ""),
          view_url: rewriteToJounieh(data?.view_url || ""),
          expires_in_days: data?.expires_in_days ?? null,
        };

        setVoucherLinks((prev) => ({ ...prev, [cacheKey]: out }));
        return out;
      } catch (e) {
        console.error("fetchVoucherLink error:", e);
        alert(`❌ Voucher link error\n${e?.message || e}`);
        return null;
      } finally {
        setVoucherBusyKey(null);
      }
    },
    [voucherLinks]
  );

  const fetchContactRowsForPhoneTerm = useCallback(
    async (searchValue, br, forceGeneric = false) => {
      const allRows = [];
      let offset = 0;
      let loops = 0;
      let hasMore = true;

      while (hasMore && loops < CONTACTS_FETCH_MAX_LOOPS) {
        const query = forceGeneric
          ? `/contacts?search=${encodeURIComponent(searchValue)}&limit=${CONTACTS_FETCH_LIMIT}&offset=${offset}`
          : `/contacts?search=${encodeURIComponent(searchValue)}&search_in=phone&limit=${CONTACTS_FETCH_LIMIT}&offset=${offset}`;

        const data = await apiJSON(query, {
          method: "GET",
          cache: "no-store",
          headers: makeBranchHeaders(br),
        });

        const rows = extractContactsArray(data);
        allRows.push(...rows);

        const pageHasMore = Boolean(data?.has_more) && rows.length > 0;
        hasMore = pageHasMore;
        offset += CONTACTS_FETCH_LIMIT;
        loops += 1;

        if (!rows.length) break;
      }

      return mergeUniqueRowsByIdOrPhone(allRows);
    },
    [makeBranchHeaders]
  );

  const fetchContactRowsForPhone = useCallback(
    async (phone, br) => {
      const terms = buildSearchTermsForContacts(phone);
      if (!terms.length) return [];

      let merged = [];

      for (const term of terms.slice(0, 6)) {
        try {
          const rows = await fetchContactRowsForPhoneTerm(term, br, false);
          merged = mergeUniqueRowsByIdOrPhone([...merged, ...rows]);
          if (classifyGuestAgainstRows(phone, merged) === "old") return merged;
        } catch (err) {
          console.warn("Phone search term failed:", term, err);
        }
      }

      for (const term of terms.slice(0, 3)) {
        try {
          const rows = await fetchContactRowsForPhoneTerm(term, br, true);
          merged = mergeUniqueRowsByIdOrPhone([...merged, ...rows]);
          if (classifyGuestAgainstRows(phone, merged) === "old") return merged;
        } catch (err) {
          console.warn("Generic search term failed:", term, err);
        }
      }

      return merged;
    },
    [fetchContactRowsForPhoneTerm]
  );

  useEffect(() => {
    if (!logs || !logs.length) return;
    if (checking) return;

    const br = (branchCode || "jounieh").toLowerCase();
    const needed = [];

    for (const log of logs) {
      for (const contact of safeArray(log?.contacts)) {
        const { phoneHref } = normalizeContact(contact);
        const key = getPhoneKey(phoneHref);
        if (!key) continue;
        const st = getStatus(br, key);
        if (!st) needed.push({ branch: br, key, rawPhone: phoneHref });
      }
    }

    if (!needed.length) return;

    const job = needed[0];
    const canonical = normalizePhoneForCompare(job.rawPhone || job.key);

    setStatus(job.branch, job.key, "checking");
    setChecking(job);

    (async () => {
      try {
        const rows = await fetchContactRowsForPhone(canonical, br);
        const result = classifyGuestAgainstRows(canonical, rows);
        setStatus(job.branch, job.key, result);
      } catch (err) {
        console.error("Error checking phone:", err);
        setStatus(job.branch, job.key, "error");
      } finally {
        setChecking(null);
      }
    })();
  }, [logs, checking, branchCode, getStatus, setStatus, fetchContactRowsForPhone]);

  useEffect(() => {
    if (!logs || !logs.length) return;
    if (webhookChecking) return;

    const br = (branchCode || "jounieh").toLowerCase();
    const needed = [];

    for (const log of logs) {
      for (const contact of safeArray(log?.contacts)) {
        const { phoneHref } = normalizeContact(contact);
        const key = getPhoneKey(phoneHref);
        if (!key) continue;

        const st = getWebhookStatus(br, key);
        if (!st) needed.push({ branch: br, key, rawPhone: phoneHref });
      }
    }

    if (!needed.length) return;

    const job = needed[0];
    setWebhookStatus(job.branch, job.key, {
      loading: true,
      messaged_before: false,
      has_replied: false,
      messages_count: 0,
      replies_count: 0,
      last_status: "",
      last_message_time: "",
      last_reply: "",
      last_reply_time: "",
    });
    setWebhookChecking(job);

    (async () => {
      try {
        const data = await apiJSON(
          `/webhooks/customer-status?phone=${encodeURIComponent(job.rawPhone || job.key)}`,
          {
            method: "GET",
            cache: "no-store",
            headers: makeBranchHeaders(br),
          }
        );

        setWebhookStatus(job.branch, job.key, {
          loading: false,
          error: false,
          messaged_before: Boolean(data?.messaged_before),
          messages_count: Number(data?.messages_count || 0),
          last_status: String(data?.last_status || "").toLowerCase(),
          last_message_time: data?.last_message_time || "",
          last_pricing_category: data?.last_pricing_category || "",
          has_replied: Boolean(data?.has_replied),
          replies_count: Number(data?.replies_count || 0),
          last_reply: data?.last_reply || "",
          last_reply_time: data?.last_reply_time || "",
        });
      } catch (err) {
        console.error("Webhook customer status check failed:", err);
        setWebhookStatus(job.branch, job.key, {
          loading: false,
          error: true,
          messaged_before: false,
          has_replied: false,
          messages_count: 0,
          replies_count: 0,
          last_status: "",
          last_message_time: "",
          last_reply: "",
          last_reply_time: "",
        });
      } finally {
        setWebhookChecking(null);
      }
    })();
  }, [
    logs,
    webhookChecking,
    branchCode,
    getWebhookStatus,
    setWebhookStatus,
    makeBranchHeaders,
  ]);

  const counts = useMemo(() => {
    const t = logs.length;
    const inserted = logs.reduce((sum, l) => sum + safeArray(l?.contacts).length, 0);

    let oldC = 0,
      newC = 0,
      unkC = 0,
      chkC = 0,
      errC = 0;

    let bdayRecent = 0;
    let bdayNext = 0;

    const br = (branchCode || "jounieh").toLowerCase();

    logs.forEach((log) => {
      safeArray(log?.contacts).forEach((contact) => {
        const { phoneHref, birthday } = normalizeContact(contact);
        const phoneKey = getPhoneKey(phoneHref);
        const st = phoneKey ? getStatus(br, phoneKey) : null;

        if (st === "old") oldC += 1;
        else if (st === "new") newC += 1;
        else if (st === "checking") chkC += 1;
        else if (st === "error") errC += 1;
        else unkC += 1;

        const flags = getOldGuestBirthdayFlags(birthday, st);
        if (flags.recent30) bdayRecent += 1;
        if (flags.next7) bdayNext += 1;
      });
    });

    return {
      tableCount: t,
      insertedCount: inserted,
      oldCount: oldC,
      newCount: newC,
      unknownCount: unkC,
      checkingCount: chkC,
      errorCount: errC,
      oldBdayRecent30: bdayRecent,
      oldBdayNext7: bdayNext,
    };
  }, [logs, branchCode, getStatus]);

  const branchLabelForMessage = useMemo(
    () => BRANCH_LABEL_MAP[branchCode] || "Jounieh",
    [branchCode]
  );

  return (
    <div className="live-feed-container">
      <MenuQrModal
        open={showQr}
        onClose={() => setShowQr(false)}
        branchCode={branchCode}
        setBranchCode={setBranchCode}
        serverLocked={serverLocked}
        hostLockedBranch={hostLockedBranch}
        BRANCH_OPTIONS={BRANCH_OPTIONS}
        BRANCH_LABEL_MAP={BRANCH_LABEL_MAP}
        BRANCH_MENU_PAGE={BRANCH_MENU_PAGE}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <h3 className="feed-title" style={{ margin: 0 }}>
          📄 <b>Data Group</b>
          <span style={{ marginLeft: 10, fontWeight: 700, fontSize: 12, opacity: 0.9 }}>
            • Branch: {BRANCH_LABEL_MAP[branchCode] || branchCode}
          </span>

          {serverLocked && (
            <span style={{ marginLeft: 10, fontWeight: 700, fontSize: 12, opacity: 0.8 }}>
              • 🔒 Locked
            </span>
          )}

          {!cleared && (
            <span style={{ marginLeft: 10, fontWeight: 600, fontSize: 13, color: "var(--muted, #94a3b8)" }}>
              • Tables: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.tableCount}</span>
              {"  "}• Inserted: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.insertedCount}</span>
              {"  "}• 🟡 OLD: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.oldCount}</span>
              {"  "}• 🟢 NEW: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.newCount}</span>
              {"  "}• 🎉 OLD BDAY ≤30d: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.oldBdayRecent30}</span>
              {"  "}• ⏭️ OLD BDAY ≤7d: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.oldBdayNext7}</span>
              {counts.checkingCount > 0 ? <>{"  "}• ⏳ Checking: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.checkingCount}</span></> : null}
              {counts.unknownCount > 0 ? <>{"  "}• ⚪ Unknown: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.unknownCount}</span></> : null}
              {counts.errorCount > 0 ? <>{"  "}• ❓ Error: <span style={{ color: "var(--text, #e5e7eb)" }}>{counts.errorCount}</span></> : null}
            </span>
          )}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <label style={{ fontSize: 11, opacity: 0.8, whiteSpace: "nowrap" }}>Branch:</label>
            <select
              value={branchCode}
              onChange={(e) => setBranchCode(e.target.value)}
              disabled={serverLocked}
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid rgba(148,163,184,.6)",
                backgroundColor: "#020617",
                color: "#e5e7eb",
                fontSize: 11,
                minWidth: 150,
                opacity: serverLocked ? 0.7 : 1,
                cursor: serverLocked ? "not-allowed" : "pointer",
              }}
              title={serverLocked ? "Branch locked by token city/host (backend truth)" : "Select branch to load its table_log"}
            >
              {BRANCH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button
              onClick={() => setShowQr(true)}
              style={{
                backgroundColor: "#0ea5e9",
                color: "#fff",
                border: "1px solid rgba(14,165,233,.85)",
                padding: "6px 12px",
                borderRadius: "8px",
                cursor: "pointer",
                boxShadow: "0 0 6px rgba(14,165,233,.35)",
                whiteSpace: "nowrap",
              }}
              title="Show QR"
            >
              📷 QR
            </button>

            <button
              onClick={() => setCleared(true)}
              style={{
                backgroundColor: "#dc3545",
                color: "#fff",
                border: "1px solid #ff4d4d",
                padding: "6px 12px",
                borderRadius: "8px",
                cursor: "pointer",
                boxShadow: "0 0 6px rgba(255, 77, 77, 0.5)",
                whiteSpace: "nowrap",
              }}
              title="Hide the feed until refresh"
            >
              🗑️ Clear Display
            </button>

            <button
              onClick={() => setCleared(false)}
              style={{
                backgroundColor: "#334155",
                color: "#fff",
                border: "1px solid rgba(148,163,184,.35)",
                padding: "6px 12px",
                borderRadius: "8px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Show feed again"
            >
              👁️ Show
            </button>
          </div>
        </div>
      </div>

      {!cleared && logs.length === 0 && <div className="empty-feed">No recent tables yet.</div>}

      {!cleared &&
        logs.map((log, index) => {
          const canCopy = canCopyLog(log);
          const br = (branchCode || "jounieh").toLowerCase();

          const perTableCounts = (() => {
            let o = 0, n = 0, u = 0, c = 0, e = 0;
            let bRecent = 0;
            let bNext = 0;

            safeArray(log?.contacts).forEach((contact) => {
              const { phoneHref, birthday } = normalizeContact(contact);
              const phoneKey = getPhoneKey(phoneHref);
              const st = phoneKey ? getStatus(br, phoneKey) : null;

              if (st === "old") o += 1;
              else if (st === "new") n += 1;
              else if (st === "checking") c += 1;
              else if (st === "error") e += 1;
              else u += 1;

              const flags = getOldGuestBirthdayFlags(birthday, st);
              if (flags.recent30) bRecent += 1;
              if (flags.next7) bNext += 1;
            });

            return { o, n, u, c, e, bRecent, bNext };
          })();

          return (
            <React.Fragment key={`${br}-${index}`}>
              <div className="chat-block">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <div><b>{log?.table || ""}</b></div>
                    <div className="chat-time" style={{ opacity: 0.8 }}>{log?.time || ""}</div>
                    <div style={{ fontSize: 12, opacity: 0.9 }}>
                      • Branch: <b>{BRANCH_LABEL_MAP[br] || br}</b>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => recheckTable(log)}
                      style={{
                        backgroundColor: "#6b7280",
                        color: "#fff",
                        border: "1px solid rgba(156,163,175,.8)",
                        padding: "4px 10px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        boxShadow: "0 0 6px rgba(148,163,184,.35)",
                        whiteSpace: "nowrap",
                        fontSize: 13,
                      }}
                      title="Re-check all numbers in this table only"
                    >
                      🔁 Recheck
                    </button>

                    <button
                      onClick={() => canCopy && copyOne(index)}
                      disabled={!canCopy}
                      style={{
                        backgroundColor: copiedIdx === index ? "#16a34a" : canCopy ? "#2563eb" : "#4b5563",
                        color: "#fff",
                        border: `1px solid ${
                          copiedIdx === index
                            ? "rgba(22,163,74,.6)"
                            : canCopy
                            ? "rgba(37,99,235,.6)"
                            : "rgba(75,85,99,.7)"
                        }`,
                        padding: "4px 10px",
                        borderRadius: "8px",
                        cursor: canCopy ? "pointer" : "not-allowed",
                        boxShadow:
                          copiedIdx === index
                            ? "0 0 6px rgba(22,163,74,.35)"
                            : canCopy
                            ? "0 0 6px rgba(37,99,235,.35)"
                            : "none",
                        whiteSpace: "nowrap",
                        fontSize: 13,
                        opacity: canCopy ? 1 : 0.6,
                      }}
                      title={canCopy ? "Copy the table exactly as shown" : "Waiting for phone checks to finish for this table..."}
                    >
                      {copiedIdx === index ? "✅ Copied" : canCopy ? "📋 Copy" : "⏳ Checking..."}
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <div>{log?.hostess || ""}</div>
                  <div>{log?.waiter || ""}</div>
                  <div style={{ opacity: 0.8 }}>{Array.isArray(log?.contacts) ? `${log.contacts.length} guests` : "0 guests"}</div>

                  <div style={{ opacity: 0.95, fontWeight: 600 }}>
                    🟡 {perTableCounts.o} • 🟢 {perTableCounts.n}
                    {perTableCounts.bRecent > 0 ? ` • 🎉 OLD≤30d ${perTableCounts.bRecent}` : ""}
                    {perTableCounts.bNext > 0 ? ` • ⏭️ OLD≤7d ${perTableCounts.bNext}` : ""}
                    {perTableCounts.c > 0 ? ` • ⏳ ${perTableCounts.c}` : ""}
                    {perTableCounts.u > 0 ? ` • ⚪ ${perTableCounts.u}` : ""}
                    {perTableCounts.e > 0 ? ` • ❓ ${perTableCounts.e}` : ""}
                  </div>
                </div>

                <div style={{ marginTop: 6 }}>
                  {safeArray(log?.contacts).map((contact, i) => {
                    const { name, phoneDisplay, phoneHref, birthday } = normalizeContact(contact);
                    const key = `${index}-${i}`;
                    const isOpen = openActionKey === key;
                    const phoneDisplayPlus = withPlus(phoneDisplay);

                    const phoneKey = getPhoneKey(phoneHref);
                    const status = phoneKey ? getStatus(br, phoneKey) : null;
                    const webhookStatus = phoneKey ? getWebhookStatus(br, phoneKey) : null;

                    const bFlags = getOldGuestBirthdayFlags(birthday, status);

                    const phoneDigits = normalizeDigits(phoneHref);
                    const voucherDate = getVoucherDateForOldBirthday(birthday, status);
                    const showVoucher =
                      status === "old" && !!voucherDate && (bFlags.recent30 || bFlags.next7) && !!phoneDigits && !!name;

                    const voucherCacheKey = showVoucher ? `${phoneDigits}|${voucherDate}` : "";
                    const voucherObj = voucherCacheKey ? voucherLinks[voucherCacheKey] : null;
                    const voucherBusy = voucherBusyKey === voucherCacheKey;

                    return (
                      <div key={key} style={{ marginBottom: 12 }}>
                        <div>{name}</div>

                        <div>
                          {phoneDisplay ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <a
                                href={buildTelHref(phoneHref)}
                                onClick={(e) => onClickPhone(e, key)}
                                style={{ textDecoration: "none", cursor: "pointer" }}
                                title="Tap to choose: Call, SMS, or WhatsApp"
                              >
                                {phoneDisplayPlus}
                              </a>

                              {phoneKey && (
                                <span
                                  className={
                                    "phone-tag " +
                                    (status === "old"
                                      ? "phone-tag-old"
                                      : status === "new"
                                      ? "phone-tag-new"
                                      : status === "error"
                                      ? "phone-tag-error"
                                      : "phone-tag-loading")
                                  }
                                >
                                  {status === "old" ? "🟡 OLD" : status === "new" ? "🟢 NEW" : status === "error" ? "❓" : "…checking"}
                                </span>
                              )}

                              {webhookStatus?.loading ? (
                                <span className="phone-tag phone-tag-loading" title="Checking webhook customer history">
                                  📡 checking webhook
                                </span>
                              ) : webhookStatus?.messaged_before ? (
                                <span
                                  className={
                                    "phone-tag " +
                                    (webhookStatus.last_status === "read"
                                      ? "phone-tag-old"
                                      : webhookStatus.last_status === "failed"
                                      ? "phone-tag-error"
                                      : "phone-tag-new")
                                  }
                                  title={
                                    `Messages: ${webhookStatus.messages_count || 0}` +
                                    `\nLast status: ${webhookStatus.last_status || "-"}` +
                                    `\nLast time: ${webhookStatus.last_message_time || "-"}`
                                  }
                                >
                                  {webhookStatus.last_status === "read"
                                    ? "👁 READ"
                                    : webhookStatus.last_status === "delivered"
                                    ? "✅ DELIVERED"
                                    : webhookStatus.last_status === "sent"
                                    ? "📩 SENT"
                                    : webhookStatus.last_status === "failed"
                                    ? "❌ FAILED"
                                    : "📩 MESSAGED"}
                                </span>
                              ) : webhookStatus && !webhookStatus.error ? (
                                <span className="phone-tag phone-tag-loading" title="No webhook delivery status found for this phone">
                                  🆕 never messaged
                                </span>
                              ) : webhookStatus?.error ? (
                                <span className="phone-tag phone-tag-error" title="Could not check webhook status">
                                  📡 webhook error
                                </span>
                              ) : null}

                              {webhookStatus?.last_template ? (
                                <span
                                  className="phone-tag phone-tag-new"
                                  title={
                                    `Template: ${webhookStatus.last_template || "-"}` +
                                    `
Sent time: ${webhookStatus.last_template_time || "-"}` +
                                    `
Language: ${webhookStatus.last_template_language || "-"}` +
                                    `
Campaign: ${webhookStatus.last_campaign || "-"}` +
                                    `
Total templates: ${webhookStatus.templates_count || 0}`
                                  }
                                >
                                  🧩 {webhookStatus.last_template}
                                </span>
                              ) : null}

                              {webhookStatus?.has_replied ? (
                                <span
                                  className="phone-tag phone-tag-old"
                                  title={
                                    `Replies: ${webhookStatus.replies_count || 0}` +
                                    `\nLast reply: ${webhookStatus.last_reply || "-"}` +
                                    `\nReply time: ${webhookStatus.last_reply_time || "-"}`
                                  }
                                >
                                  💬 REPLIED
                                </span>
                              ) : null}

                              {status === "old" && bFlags.recent30 ? (
                                <span
                                  style={{
                                    fontSize: 12,
                                    padding: "3px 8px",
                                    borderRadius: 999,
                                    border: "1px solid rgba(234,179,8,.35)",
                                    background: "rgba(234,179,8,.15)",
                                    color: "#fde68a",
                                    fontWeight: 700,
                                  }}
                                  title={`Birthday was ${bFlags.recentDaysAgo} day(s) ago`}
                                >
                                  🎉 BDAY {bFlags.recentDaysAgo}d ago
                                </span>
                              ) : null}

                              {status === "old" && bFlags.next7 ? (
                                <span
                                  style={{
                                    fontSize: 12,
                                    padding: "3px 8px",
                                    borderRadius: 999,
                                    border: "1px solid rgba(34,197,94,.35)",
                                    background: "rgba(34,197,94,.12)",
                                    color: "#bbf7d0",
                                    fontWeight: 700,
                                  }}
                                  title={`Birthday is in ${bFlags.nextInDays} day(s)`}
                                >
                                  ⏭️ BDAY in {bFlags.nextInDays}d
                                </span>
                              ) : null}

                              {showVoucher ? (
                                <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                  {!voucherObj ? (
                                    <button
                                      onClick={async () => {
                                        await fetchVoucherLink({ date: voucherDate, name, phoneDigits });
                                      }}
                                      disabled={voucherBusy}
                                      style={{
                                        background: voucherBusy ? "#334155" : "#7c3aed",
                                        color: "#fff",
                                        border: "1px solid rgba(124,58,237,.6)",
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        cursor: voucherBusy ? "not-allowed" : "pointer",
                                        fontSize: 12,
                                        fontWeight: 800,
                                        opacity: voucherBusy ? 0.75 : 1,
                                      }}
                                      title={`Create secure voucher link for ${voucherDate}`}
                                    >
                                      {voucherBusy ? "⏳ Voucher…" : "🎟 Voucher"}
                                    </button>
                                  ) : (
                                    <>
                                      <a
                                        href={voucherObj.page_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          background: "rgba(124,58,237,.16)",
                                          border: "1px solid rgba(124,58,237,.35)",
                                          color: "#ddd6fe",
                                          padding: "4px 10px",
                                          borderRadius: 999,
                                          textDecoration: "none",
                                          fontSize: 12,
                                          fontWeight: 800,
                                        }}
                                        title={`Open voucher (${voucherDate})`}
                                      >
                                        🎟 Open Voucher
                                      </a>

                                      <button
                                        onClick={() => copyText(voucherObj.page_url)}
                                        style={{
                                          background: "rgba(59,130,246,.16)",
                                          border: "1px solid rgba(59,130,246,.35)",
                                          color: "#bfdbfe",
                                          padding: "4px 10px",
                                          borderRadius: 999,
                                          cursor: "pointer",
                                          fontSize: 12,
                                          fontWeight: 800,
                                        }}
                                        title="Copy voucher link"
                                      >
                                        📋 Copy
                                      </button>

                                      <span style={{ fontSize: 11, opacity: 0.8 }}>({voucherDate})</span>
                                    </>
                                  )}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            ""
                          )}
                        </div>

                        {isOpen && phoneDisplay && (
                          <div
                            className="phone-actions"
                            style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <a href={buildTelHref(phoneHref)} style={btnStyle("#16a34a")} title="Call" onClick={() => setOpenActionKey(null)}>
                              📞 Call
                            </a>
                            <a href={buildSmsHref(phoneHref)} style={btnStyle("#2563eb")} title="SMS" onClick={() => setOpenActionKey(null)}>
                              ✉️ SMS
                            </a>
                            <a
                              href={buildWaHref(phoneHref)}
                              style={btnStyle("#25D366")}
                              title="WhatsApp"
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => setOpenActionKey(null)}
                            >
                              🟢 WhatsApp
                            </a>
                            <a
                              href={buildWaFeedbackHref(phoneHref, name, branchLabelForMessage)}
                              style={btnStyle("#15803d")}
                              title={`Send feedback request on WhatsApp (${branchLabelForMessage})`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => setOpenActionKey(null)}
                            >
                              💬 Feedback
                            </a>
                          </div>
                        )}

                        {birthday ? <div>🎂 {birthday}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
              <hr style={{ border: "1px solid #333", margin: "10px 0" }} />
            </React.Fragment>
          );
        })}
    </div>
  );
}